/**
 * Wave 316 · Reaction bar — curated 6-code set under each message.
 *
 * UI shape:
 *   - Always visible: pills for any reaction that already exists on this
 *     message (count + glyph). Clicking your own pill removes it; clicking
 *     a peer's pill adds yours to it (toggling).
 *   - "+" button reveals the full picker (6 emojis). One click adds.
 *   - Optimistic update: we mutate the local aggregate immediately and
 *     reconcile with the server's reply (which is the canonical post-mutate
 *     state).
 *
 * Why curated and not freeform:
 *   - DB space: 2-byte ASCII codes vs. 4–7-byte emoji glyphs.
 *   - UX: 6 reactions cover 99% of acks (👍 ❤️ 🎉 👀 🔥 ✅) and avoid the
 *     "what's the right one?" decision paralysis.
 *   - Housy stays out: this component never imports the agent client.
 */

import { useState } from 'react';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

export type ReactionCode = 'tu' | 'hr' | 'td' | 'ey' | 'fr' | 'ck';

export interface ReactionAggregate {
  messageId: string;
  code: ReactionCode;
  count: number;
  reactedByViewer: boolean;
}

// Curated set with the glyph + a one-word hint shown on hover (title attr).
// Order is intentional — most-common acks first.
export const REACTION_DEFS: Array<{
  code: ReactionCode;
  glyph: string;
  hintPt: string;
  hintEn: string;
}> = [
  { code: 'tu', glyph: '👍', hintPt: 'Curtir', hintEn: 'Like' },
  { code: 'hr', glyph: '❤️', hintPt: 'Adorei', hintEn: 'Love' },
  { code: 'td', glyph: '🎉', hintPt: 'Comemorar', hintEn: 'Celebrate' },
  { code: 'ey', glyph: '👀', hintPt: 'Vi · estou de olho', hintEn: 'Watching' },
  { code: 'fr', glyph: '🔥', hintPt: 'Massa', hintEn: 'Fire' },
  { code: 'ck', glyph: '✅', hintPt: 'Feito · OK', hintEn: 'Done · OK' },
];

// Glyph lookup so the pills can render the right emoji from a code.
const GLYPH: Record<ReactionCode, string> = REACTION_DEFS.reduce(
  (acc, d) => ({ ...acc, [d.code]: d.glyph }),
  {} as Record<ReactionCode, string>,
);

export function ReactionBar({
  messageId,
  reactions,
  onChange,
  align = 'left',
}: {
  messageId: string;
  reactions: ReactionAggregate[];
  /** Called with the post-mutation aggregate so the parent can swap state. */
  onChange: (next: ReactionAggregate[]) => void;
  align?: 'left' | 'right';
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Wave 316b · defensive — qualquer parent que passe `undefined` ou um
  // shape inesperado não derruba a UI. Reações são ornamento; quando
  // duvidoso, mostra a barra vazia.
  const safeReactions = Array.isArray(reactions) ? reactions : [];

  // Filter to just this message + sort by code (so pill order is stable
  // between renders).
  const mine = safeReactions
    .filter((r) => r.messageId === messageId)
    .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));

  const toggleCode = async (code: ReactionCode) => {
    if (busy) return;
    setBusy(true);
    setPickerOpen(false);
    const existing = mine.find((r) => r.code === code);
    try {
      // Wave 316b · BUG FIX: request<T> already unwraps `data`, so the
      // response IS the array. Era `resp.data`, virou `resp`. Crash
      // anterior: `Cannot read properties of undefined (reading 'filter')`
      // porque `resp.data` era undefined e o `[...others, ...resp.data]`
      // explodia. Defensivo extra: `Array.isArray` antes de espalhar.
      const resp = existing?.reactedByViewer
        ? await api.removeReaction(messageId, code)
        : await api.addReaction(messageId, code);
      const respArr = Array.isArray(resp) ? resp : [];
      const others = safeReactions.filter((r) => r.messageId !== messageId);
      onChange([...others, ...respArr]);
    } catch {
      // Silent — reaction failure is low-stakes; user can re-tap.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'mt-1 flex flex-wrap items-center gap-1',
        align === 'right' && 'justify-end',
      )}
    >
      {mine.map((r) => (
        <button
          key={r.code}
          type="button"
          onClick={() => void toggleCode(r.code)}
          disabled={busy}
          title={`${(isEn ? (REACTION_DEFS.find((d) => d.code === r.code)?.hintEn ?? '') : (REACTION_DEFS.find((d) => d.code === r.code)?.hintPt ?? ''))} · ${r.count}`}
          className={cn(
            'pressable-soft inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]',
            'border transition-colors duration-150 ease',
            r.reactedByViewer
              ? 'border-brand-400/50 bg-brand-400/10 text-ink'
              : 'border-line bg-surface-2 text-ink-muted hover:bg-surface-3 hover:text-ink',
          )}
        >
          <span className="text-[13px] leading-none">{GLYPH[r.code]}</span>
          <span className="tabular-nums">{r.count}</span>
        </button>
      ))}

      <div className="relative">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          disabled={busy}
          aria-label={isEn ? 'Add reaction' : 'Adicionar reação'}
          title={isEn ? 'React' : 'Reagir'}
          className={cn(
            'pressable-soft focus-ring inline-flex h-[22px] items-center rounded-full border border-line bg-surface-2 px-1.5 text-[11px] text-ink-muted',
            'hover:bg-surface-3 hover:text-ink',
          )}
        >
          <svg
            className="h-3 w-3"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden
          >
            <circle cx="8" cy="8" r="6" />
            <path d="M5.5 9c.5.8 1.4 1.3 2.5 1.3S9.9 9.8 10.5 9" />
            <circle cx="6" cy="6.7" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="10" cy="6.7" r="0.6" fill="currentColor" stroke="none" />
          </svg>
        </button>
        {pickerOpen && (
          <div
            // Wave 316 · Picker pop. transform-origin alinhado com trigger
            // pra animar do botão "+", não do centro (emil rule).
            className={cn(
              'absolute z-20 mt-1 flex items-center gap-0.5 rounded-full',
              'border border-line bg-surface-2 px-1 py-0.5 shadow-pop dark:shadow-pop-dark',
              'animate-scale-in',
              align === 'right' ? 'right-0' : 'left-0',
            )}
            style={{ transformOrigin: align === 'right' ? 'top right' : 'top left' }}
            role="menu"
          >
            {REACTION_DEFS.map((d) => {
              const youAlready = mine.some(
                (r) => r.code === d.code && r.reactedByViewer,
              );
              return (
                <button
                  key={d.code}
                  type="button"
                  role="menuitem"
                  onClick={() => void toggleCode(d.code)}
                  title={isEn ? d.hintEn : d.hintPt}
                  disabled={busy}
                  className={cn(
                    'pressable-soft rounded-full px-1.5 py-0.5 text-[15px] leading-none',
                    'transition-colors duration-150 ease',
                    youAlready ? 'bg-brand-400/15' : 'hover:bg-surface-3',
                  )}
                >
                  {d.glyph}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
